import ModeToggle from '@/components/common/ModeToggle';
import LearnerSidebar from '@/components/layout/LearnerSidebar';
import CourseSearchModal from '@/components/learner/CourseSearchModal';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { useAuth } from '@/contexts/AuthContext';
import { authService } from '@/services/auth.service';
import { businessService } from '@/services/business.service';
import useHasPermission from '@/hooks/useHasPermission';
import {
  LEARNER_CONTACT_SUPPORT_PERMISSION,
  LEARNER_COURSES_PERMISSION,
  LEARNER_CERTIFICATIONS_PERMISSION,
  LEARNER_LEARNING_PATHS_PERMISSION,
} from '@/constants/constant-permissions';
import {
  ChevronDown,
  FileText,
  GraduationCap,
  HelpCircle,
  LogOut, Menu,
  Palette,
  Search,
  User2,
  X
} from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { Link, Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

export default function LearnerLayout() {
  const canViewHelpSupport = useHasPermission([LEARNER_CONTACT_SUPPORT_PERMISSION.LEARNER_CONTACT_SUPPORT_READ]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Mobile sidebar
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Load collapsed state from localStorage
    const saved = localStorage.getItem('learnerSidebarCollapsed');
    return saved === 'true';
  }); // Desktop sidebar collapse
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [gradientEnabled, setGradientEnabled] = useState(() => {
    // Load gradient preference from localStorage
    const saved = localStorage.getItem('gradientTheme');
    return saved === 'true' || saved === null; // Default to true if not set
  });
  const { user } = useAuth();

  // Show search only if user has write permission for at least one of courses/certifications/learning paths
  const canSearchCourses = useHasPermission([LEARNER_COURSES_PERMISSION.LEARNER_COURSES_WRITE]);
  const canSearchCertifications = useHasPermission([LEARNER_CERTIFICATIONS_PERMISSION.LEARNER_CERTIFICATIONS_WRITE]);
  const canSearchLearningPaths = useHasPermission([LEARNER_LEARNING_PATHS_PERMISSION.LEARNER_LEARNING_PATHS_WRITE]);
  const showSearch = canSearchCourses || canSearchCertifications || canSearchLearningPaths;

  // Fetch business settings for logos
  const { data: businessData } = useQuery({
    queryKey: ['businessSettings'],
    queryFn: () => businessService.getBusinessSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  // Toggle gradient theme
  const toggleGradientTheme = () => {
    const newState = !gradientEnabled;
    setGradientEnabled(newState);
    localStorage.setItem('gradientTheme', newState.toString());
  };
  
  // Save collapsed state to localStorage whenever it changes
  const toggleSidebarCollapsed = () => {
    const newState = !isSidebarCollapsed;
    setIsSidebarCollapsed(newState);
    localStorage.setItem('learnerSidebarCollapsed', newState.toString());
  };

  // Get user details from the auth context
  const userName = user ? `${user.given_name || ''} ${user.family_name || ''}`.trim() || user.full_name || 'Learner' : 'Learner';
  const userEmail = user?.email || 'email@example.com';

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setShowProfileMenu(false);
    
    // Show loading toast
    const loadingToast = toast.loading('Logging out...');
    
    try {
      await authService.logout();
      toast.dismiss(loadingToast);
      toast.success('Logged out successfully');
      window.location.href = '/login';
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Logout failed. Please try again.');
      setIsLoggingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Logout overlay */}
      {isLoggingOut && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-lg font-semibold text-gray-900">Logging out...</p>
            <p className="text-sm text-gray-500">Please wait while we sign you out securely</p>
          </div>
        </div>
      )}
      
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 xl:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Gradient theme: sky blue fading to white. Solid at every width — below
          xl the sidebar slides over the page, so it can't be see-through. */}
      <aside className={`fixed top-0 left-0 h-full ${
        gradientEnabled
          ? 'bg-white bg-gradient-to-b from-sky-100 via-sky-50 to-white shadow-xl border-r border-sky-200/70'
          : 'bg-white shadow-lg'
      } transform transition-all duration-300 z-50 ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } xl:translate-x-0 ${isSidebarCollapsed ? 'xl:w-20' : 'xl:w-64'} w-64`} style={{ overflow: 'visible' }}>
        <div className="flex flex-col h-full relative">
          <div className={`flex items-center justify-between gap-2 p-4 border-b ${
            gradientEnabled
              ? 'border-sky-200/70 bg-gradient-to-r from-sky-100 to-white'
              : 'border-gray-200'
          }`}>
            {/* "Collapsed" is a desktop (xl) state, but it is remembered in
                localStorage — so it used to follow the learner onto a phone,
                where the sidebar is always full width, and the full logo was
                replaced by the small one (or just a cap icon). The small logo
                is now xl-only; below xl the full logo always shows. */}
            <Link
              to="/learn"
              className="flex items-center gap-2 min-w-0 flex-1 xl:justify-center"
            >
              {isSidebarCollapsed && (
                // Collapsed (xl only) → Small logo or GraduationCap
                <span className="hidden xl:flex items-center justify-center">
                  {businessData?.data?.small_logo_url ? (
                    <span className="w-12 h-12 flex items-center justify-center">
                      <img
                        src={businessData.data.small_logo_url}
                        alt={businessData.data.company_name || 'Company Logo'}
                        className="w-full h-full object-cover"
                      />
                    </span>
                  ) : (
                    <GraduationCap className="w-8 h-8 text-primary-700" />
                  )}
                </span>
              )}
              {/* Expanded → Business logo or default Skillzen logo */}
              <div className={`${isSidebarCollapsed ? 'flex xl:hidden' : 'flex'} items-center justify-center w-full h-16 px-2`}>
                <img
                  src={businessData?.data?.large_logo_url || '/logo/Skillzen Final Logo-01.png'}
                  alt={businessData?.data?.company_name || 'Skillzen'}
                  className="max-w-full max-h-14 object-contain"
                />
              </div>
            </Link>

            <button
              onClick={() => setIsSidebarOpen(false)}
              className="xl:hidden flex-shrink-0 text-gray-500 hover:text-gray-700"
              aria-label="Close menu"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <LearnerSidebar 
            isOpen={isSidebarOpen} 
            isCollapsed={isSidebarCollapsed} 
            onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
            onClose={() => setIsSidebarOpen(false)}
            gradientEnabled={gradientEnabled}
          />
        </div>
      </aside>

      <div className={`transition-all duration-300 ${isSidebarCollapsed ? 'xl:ml-20' : 'xl:ml-64'}`}>
        <header className="bg-white shadow-sm border-b sticky top-0 z-30">
          <div className="flex items-center justify-between px-3 lg:px-4 py-2 lg:py-3">
            <div className="flex items-center gap-2 sm:gap-4">
              {/* Desktop burger menu for sidebar toggle */}
              <button
                onClick={toggleSidebarCollapsed}
                className="hidden xl:block p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Mobile burger menu */}
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="xl:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {showSearch && (
                <div className="relative hidden md:block">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search"
                    onClick={() => setShowSearchModal(true)}
                    readOnly
                    className="pl-10 pr-4 py-2 w-48 lg:w-64 xl:w-96 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:bg-white cursor-pointer hover:bg-gray-100 transition-colors"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2 lg:gap-3 flex-shrink-0">
              {/* Mobile Search Button */}
              {showSearch && (
                <button
                  onClick={() => setShowSearchModal(true)}
                  className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Search courses"
                >
                  <Search className="w-5 h-5" />
                </button>
              )}

              {/* Mode Toggle - Only shows for users with both roles */}
              <ModeToggle />

              {/* Docs Button */}
              <button
                onClick={() => window.open('/docs', '_blank')}
                className="hidden sm:flex items-center gap-2 px-2.5 lg:px-3 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 border border-gray-200 hover:border-indigo-200 rounded-lg transition-colors"
                title="Open documentation"
              >
                <FileText className="w-4 h-4" />
                {/* Icon-only on tablets, where the header has no room for labels */}
                <span className="hidden lg:inline text-sm font-medium">Docs</span>
              </button>

              {/* Gradient Theme Toggle */}
              {/* <button
                onClick={toggleGradientTheme}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title={gradientEnabled ? 'Disable gradient theme' : 'Enable gradient theme'}
              >
                <Palette className={`w-5 h-5 ${gradientEnabled ? 'text-primary-600' : 'text-gray-500'}`} />
              </button> */}

              <NotificationBell isLearner />

              {/* Profile Menu */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded-lg"
                >
                  <img
                    src={user?.profile_image || `https://ui-avatars.com/api/?name=${userName}&background=3b82f6&color=fff`}
                    alt={userName}
                    className="w-8 h-8 rounded-full"
                  />
                  {/* Name only from lg (it wrapped to two lines on tablets);
                      it is also shown at the top of the profile menu. */}
                  <div className="hidden lg:block text-left">
                    <p className="text-sm font-medium text-gray-900 whitespace-nowrap max-w-[160px] truncate">{userName}</p>
                  </div>
                  <ChevronDown className="hidden sm:block w-4 h-4 text-gray-500" />
                </button>

                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 max-w-[calc(100vw-1.5rem)] bg-white rounded-lg shadow-lg border z-50">
                    <div className="p-3 border-b">
                      <p className="text-sm font-medium text-gray-900">{userName}</p>
                      <p className="text-xs text-gray-500 truncate">{userEmail}</p>
                    </div>
                    <div className="p-2">
                      <Link
                        to="/learn/profile"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                      >
                        <User2 className="w-4 h-4" />
                        My Profile
                      </Link>
                      {/* <Link
                        to="/learn/settings"
                        onClick={() => setShowProfileMenu(false)}
                        className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                      >
                        <Settings className="w-4 h-4" />
                        Settings
                      </Link> */}
                      <button
                        onClick={toggleGradientTheme}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <Palette className="w-4 h-4" />
                          <span>Gradient Theme</span>
                        </div>
                        <div className={`w-9 h-5 rounded-full transition-colors ${
                          gradientEnabled ? 'bg-primary-600' : 'bg-gray-300'
                        } relative`}>
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${
                            gradientEnabled ? 'translate-x-4' : 'translate-x-0'
                          }`} />
                        </div>
                      </button>
                      {canViewHelpSupport && (
                        <Link
                          to="/learn/help"
                          onClick={() => setShowProfileMenu(false)}
                          className="flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
                        >
                          <HelpCircle className="w-4 h-4" />
                          Help & Support
                        </Link>
                      )}
                      <button
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors ${
                          isLoggingOut 
                            ? 'text-gray-400 bg-gray-50 cursor-not-allowed' 
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        {isLoggingOut ? (
                          <>
                            <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                            Logging out...
                          </>
                        ) : (
                          <>
                            <LogOut className="w-4 h-4" />
                            Logout
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="p-3 sm:p-4 lg:p-5 xl:p-6">
          <Outlet />
        </main>
      </div>

      {/* Course Search Modal */}
      <CourseSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
      />
    </div>
  );
}